// One resource: its details, its availability, and what it is booked on.
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  scheduling,
  type BookingInWay,
  type LabelledBooking,
  type Resource,
  type ResourceStatus,
  type Unavailability,
} from '../schedulingApi';
import { ApiError, api, type User } from '../api';
import { dateRangeLabel, formatClockRange, formatDMY, todayISO } from '../lib/dates';
import { bookingLabel } from '../lib/bookingLabel';
import { Banner, Field } from '../ui';
import { KIND_LABEL, groupForKind } from '../lib/resourceGroups';
import { Modal, PageHeader, StatusBadge } from '../components/ui';
import { CardList, RowCard } from '../components/ui/RowCard';
import { Documents } from '../components/Documents';
import { MetersPanel, SellPanel, ServiceHistoryPanel } from '../components/FleetPanels';
import { LinkedSubmissions } from '../components/forms/LinkedSubmissions';
import { useSession } from '../session';
import { documents, type Attachment } from '../documentsApi';
import {
  competencyCodes,
  inductionCodes,
  people,
  type CompetencyCode,
  type InductionCode,
  type PersonRecord,
} from '../peopleApi';
import {
  ASSET_SECTIONS,
  PERSON_SECTIONS,
  RESOURCE_TAB_KEYS,
  folderOf,
} from '../lib/personSections';
import { ASSET_FOLDERS } from '../lib/attachmentFolders';
import { useTabParam } from '../lib/useTabParam';
import { EditResourceModal } from '../components/resources/EditResourceModal';
import { InsurancesSection } from '../components/resources/InsurancesSection';
import { HOLDS_INSURANCES, insurances, type InsuranceRecord } from '../insuranceApi';
import { machineTypes, type MachineType } from '../fleetApi';
import { NeedsAttentionCard } from '../components/people/NeedsAttentionCard';
import { RecordsSection } from '../components/people/RecordsSection';
import { FolderSection } from '../components/people/FolderSection';

export function ResourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useSession();
  const [resource, setResource] = useState<Resource | null>(null);
  const [unavailability, setUnavailability] = useState<Unavailability[]>([]);
  const [bookings, setBookings] = useState<LabelledBooking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // A person's page is tabbed; every other kind keeps the flat stack of panels
  // it has always had. The hook runs unconditionally — hooks always do — and
  // its value is simply unused on the other kinds.
  // Both sets of keys: the kind is not known when this runs.
  const [tab, setTab] = useTabParam(RESOURCE_TAB_KEYS);
  const [records, setRecords] = useState<PersonRecord[]>([]);
  const [insuranceRecords, setInsuranceRecords] = useState<InsuranceRecord[]>([]);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [codes, setCodes] = useState<CompetencyCode[]>([]);
  const [inductionList, setInductionList] = useState<InductionCode[]>([]);

  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  // Listing users is admin-only. A manager gets no picker — and the edit form
  // must then leave the link alone rather than send "not linked".
  const [canLinkAccounts, setCanLinkAccounts] = useState(false);
  // Null = the list could not be loaded, and the edit form OMITS the picker
  // (and the field) rather than sending a blank that would strip the type.
  const [machineTypeOptions, setMachineTypeOptions] = useState<MachineType[] | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const result = await scheduling.getResource(id);
      setResource(result.resource);
      setUnavailability(result.unavailability);
      setBookings(result.bookings);
      setError(null);

      if (result.resource.kind === 'person') {
        // Four requests, in parallel: the records, the documents that hang
        // off them, and both code lists the add dialogs need. The page cannot
        // draw a folder without them, so there is nothing to be gained by
        // staggering them.
        const [recordResult, fileResult, codeResult, inductionResult] = await Promise.all([
          people.records(id),
          documents.list('resource', id),
          competencyCodes.list(),
          inductionCodes.list(),
        ]);
        setRecords(recordResult.records);
        setFiles(fileResult.attachments);
        setCodes(codeResult.codes);
        setInductionList(inductionResult.codes);
      }

      // A subcontractor's insurances (round 41), and the documents beside
      // them — the panel finds each record's certificates by link.
      if (HOLDS_INSURANCES.includes(result.resource.kind)) {
        const [insuranceResult, fileResult] = await Promise.all([
          insurances.list(id),
          documents.list('resource', id),
        ]);
        setInsuranceRecords(insuranceResult.records);
        setFiles(fileResult.attachments);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that resource.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // The pickers the edit form needs. Loaded once, and separately from the
  // resource: a manager's 403 on the user list is an ordinary outcome here,
  // not a failure worth putting on screen.
  useEffect(() => {
    void api
      .listUsers()
      .then((r) => {
        setUsers(r.users);
        setCanLinkAccounts(true);
      })
      .catch(() => setCanLinkAccounts(false));
    // Archived included: a machine already holding a retired type must be
    // able to re-save it (the edit form offers it labelled "(archived)").
    void machineTypes
      .list({ includeArchived: true })
      .then((r) => setMachineTypeOptions(r.machineTypes))
      .catch(() => setMachineTypeOptions(null));
  }, []);

  async function changeStatus(status: ResourceStatus) {
    if (!id) return;
    try {
      const result = await scheduling.updateResource(id, { status });
      if (result.affectedBookings.length > 0) {
        setNotice(
          `Saved. ${result.affectedBookings.length} booking${
            result.affectedBookings.length === 1 ? '' : 's'
          } now need re-planning: ${result.affectedBookings.map(bookingLabel).join(', ')}.`
        );
      } else {
        setNotice('Saved.');
      }
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the status.');
    }
  }

  if (!resource) {
    return error ? <Banner kind="error">{error}</Banner> : <p className="muted">Loading…</p>;
  }

  const group = groupForKind(resource.kind);
  const isAsset = resource.kind === 'plant' || resource.kind === 'vehicle';
  const isPerson = resource.kind === 'person';
  const canEdit = user?.role === 'manager' || user?.role === 'admin';

  /**
   * Machines are tabbed too, from round 34.
   *
   * Round 31 decided the opposite — a machine's page is meters, service
   * history, prestarts and availability, and folders are groups inside its one
   * documents panel rather than tabs. That reasoning was about the DOCUMENTS,
   * and it survives: the folders are still groups. What did not survive is the
   * page it sat on. Nathan walked it as a customer and found four full panels
   * stacked down a screen, with the service history below the fold and the
   * prestarts below that — the same complaint that tabbed the job page in
   * round 33 and the person page in round 27. The app now has one shape for a
   * detail page.
   *
   * A subcontractor, supplier or "other" has almost nothing on its page, so it
   * stays a plain stack: tabs over two short panels would be furniture.
   */
  const tabbed = isPerson || isAsset;
  const showing = (key: string) => !tabbed || tab === key;

  const filesIn = (category: string) =>
    files.filter((f) => folderOf(f.category) === category);
  const recordsIn = (category: string) => records.filter((r) => r.category === category);

  const tabCount = (section: (typeof PERSON_SECTIONS)[number]): number | null => {
    if (section.recordCategory) return recordsIn(section.recordCategory).length;
    if (section.attachmentCategory) return filesIn(section.attachmentCategory).length;
    return null;
  };

  return (
    <>
      {/* PageHeader rather than a hand-rolled `.row`: a bare `.row` is
          `items-end`, so a button taller than the heading hangs down through
          the heading's own bottom margin and finishes flush against the
          Overview panel below it. The component carries the gap on the row
          and zeroes the heading, which is the whole reason it exists.

          The title is the label, not the bare name: the identifier is how
          someone standing next to the machine knows they are on the right
          page. */}
      <PageHeader
        title={resource.label}
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
              Edit
            </button>
            {/* Back to the group it belongs to rather than the landing page —
                one fewer click to the list they came from. */}
            <Link to={`/app/resources/${group.path}`} className="btn-secondary">
              Back to {group.label.toLowerCase()}
            </Link>
          </>
        }
      />

      {editing && (
        <EditResourceModal
          resource={resource}
          users={users}
          canLinkAccounts={canLinkAccounts}
          machineTypes={machineTypeOptions}
          onClose={() => setEditing(false)}
          onSaved={(message) => {
            setEditing(false);
            setNotice(message);
            setError(null);
            void load();
          }}
        />
      )}

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      {/* The tab strip, derived from PERSON_SECTIONS so the labels, the counts
          and the folders an upload lands in cannot drift apart. Scrolls
          sideways on a phone rather than wrapping into two rows. */}
      {tabbed && (
        <div className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200">
          {(isPerson ? PERSON_SECTIONS : ASSET_SECTIONS).map((section) => {
            const count = tabCount(section);
            const active = tab === section.key;
            return (
              <button
                key={section.key}
                type="button"
                onClick={() => setTab(section.key)}
                aria-current={active ? 'page' : undefined}
                className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                  active
                    ? 'border-brand-700 font-medium text-brand-800'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {section.label}
                {count !== null && count > 0 && (
                  <span className="ml-1 text-xs text-gray-500">{count}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {isPerson && tab === 'overview' && (
        <NeedsAttentionCard
          records={records}
          onJumpTo={(record) =>
            setTab(record.category === 'competency' ? 'competencies' : 'inductions')
          }
        />
      )}

      {showing('overview') && (
      <section className="panel">
        <h2>Overview</h2>
        <dl className="details">
          <dt>Kind</dt>
          <dd>{KIND_LABEL[resource.kind]}</dd>
          {/* The fleet fields, shown only on the kinds that can hold them.
              A crew member with an empty "Rego —" row would be reading a
              question the product never intends to ask about them. */}
          {isAsset && (
            <>
              <dt>ID</dt>
              <dd>{resource.identifier ?? <span className="muted">—</span>}</dd>
              <dt>Registration</dt>
              <dd>
                {resource.registrationNumber ?? <span className="muted">—</span>}
                {resource.registrationExpiry && (
                  <>
                    {' '}
                    <span className="muted">until {formatDMY(resource.registrationExpiry)}</span>{' '}
                    <StatusBadge kind="expiry" status={resource.registrationExpiryStatus} />
                  </>
                )}
              </dd>
              <dt>Next service</dt>
              <dd>
                {resource.nextServiceDate ? formatDMY(resource.nextServiceDate) : <span className="muted">—</span>}
                {resource.serviceIntervalValue !== null && (
                  <span className="muted">
                    {' '}
                    (every {resource.serviceIntervalValue} {resource.serviceIntervalUnit})
                  </span>
                )}
              </dd>
            </>
          )}
          {/* The disposal record outlives everything else on the row — it is
              never cleared by an edit — so it is shown whenever it is set,
              whatever the kind has since become. */}
          {resource.soldDate && (
            <>
              <dt>Sold</dt>
              <dd>
                {formatDMY(resource.soldDate)}
                {resource.soldTo ? ` to ${resource.soldTo}` : ''}
              </dd>
            </>
          )}
          <dt>{isPerson ? 'Position' : 'Category'}</dt>
          <dd>{resource.category ?? <span className="muted">—</span>}</dd>
          <dt>Notes</dt>
          <dd>{resource.notes ?? <span className="muted">—</span>}</dd>
        </dl>

        <div className="row">
          {/* Named classes rather than a bare element. The active one used to
              be rendered with an empty className, which `legacy.css`'s
              `:not([class])` guard correctly declines to style — so the one
              button on this row that says what the resource IS came out as an
              unstyled 24px browser default. Found at 375px, 2026-08-26. */}
          {(['available', 'unavailable', 'archived'] as ResourceStatus[]).map((status) => (
            <button
              key={status}
              type="button"
              className={
                resource.status === status ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'
              }
              disabled={resource.status === status}
              onClick={() => void changeStatus(status)}
            >
              {status}
            </button>
          ))}
          {isAsset && (
            <>
              <span className="spacer" />
              <SellPanel
                resource={resource}
                onSold={(msg) => {
                  setNotice(msg);
                  void load();
                }}
              />
            </>
          )}
        </div>
        <p className="hint">
          Only an available resource can be booked. Switching to unavailable or archived does not
          delete anything; existing bookings stay and get flagged for re-planning.
        </p>
      </section>
      )}

      {/* A subcontractor's insurances (round 41), straight after Overview —
          the compliance question is the first thing this page exists to
          answer about a subbie. The plain stack stands (round 34: tabs over
          short panels are furniture), so no `showing()` gate. */}
      {HOLDS_INSURANCES.includes(resource.kind) && (
        <InsurancesSection
          resourceId={resource.id}
          records={insuranceRecords}
          files={files}
          canEdit={canEdit}
          onChanged={(message) => {
            setNotice(message);
            setError(null);
            void load();
          }}
          onError={(message) => {
            setError(message);
            setNotice(null);
          }}
        />
      )}

      {/* Assets only. A person has no meters and no service history, and an
          empty panel saying so is a question the product never means to ask
          about them. */}
      {isAsset && showing('service') && (
        <>
          <MetersPanel
            resource={resource}
            onChanged={(msg) => {
              setNotice(msg);
              void load();
            }}
          />
          <ServiceHistoryPanel
            resource={resource}
            onChanged={(msg) => {
              setNotice(msg);
              void load();
            }}
          />
        </>
      )}

      {/* The prestart history, on its own tab beside the machine it is about. */}
      {isAsset && showing('forms') && <LinkedSubmissions resourceId={resource.id} showWhenEmpty />}

      {/* A person's Away panel sits on its own tab (round 36), where a
          machine keeps it on Schedule. "Coming up" below deliberately stays
          on the person's Overview — Nathan asked for the AWAY section on a
          tab, and upcoming bookings are the first thing Overview should
          answer about a person anyway. */}
      {(isAsset ? showing('schedule') : showing('away')) && (
      <AvailabilityPanel
        resourceId={resource.id}
        resourceName={resource.name}
        entries={unavailability}
        onChanged={(message) => {
          setNotice(message);
          setError(null);
          void load();
        }}
        onError={(message) => {
          // A refusal (leave on leave, round 26) is now a routine answer here,
          // and an earlier "Saved." left standing beside it reads as "it saved
          // anyway". One message at a time.
          setError(message);
          setNotice(null);
        }}
      />
      )}

      {/* A person's documents live in folders, beside the records they are
          evidence for (round 27). Every other kind keeps the one flat list:
          plant and vehicle documents were deliberately left alone. */}
      {isPerson ? (
        // Only the tabs that hold documents: Overview and Away carry no
        // folder, and mapping them here would draw an empty folder section
        // for a shelf that does not exist (round 36's away tab found this).
        PERSON_SECTIONS.filter((s) => s.key === tab && s.attachmentCategory).map((section) =>
          section.recordCategory ? (
            <RecordsSection
              key={section.key}
              section={section}
              resourceId={resource.id}
              records={recordsIn(section.recordCategory)}
              files={filesIn(section.attachmentCategory!)}
              codes={codes}
              inductions={inductionList}
              canEdit={canEdit}
              onChanged={(message) => {
                setNotice(message);
                setError(null);
                void load();
              }}
              onError={(message) => {
                setError(message);
                setNotice(null);
              }}
            />
          ) : (
            <FolderSection
              key={section.key}
              section={section}
              resourceId={resource.id}
              files={filesIn(section.attachmentCategory!)}
              canEdit={canEdit}
              onChanged={(message) => {
                setNotice(message);
                setError(null);
                void load();
              }}
              onError={(message) => {
                setError(message);
                setNotice(null);
              }}
            />
          )
        )
      ) : (
        /* Rego, manuals, plant risk assessments, invoices. Expiry flags
           inform; they never block a booking (spec 03).

           A machine's documents draw in folders inside this one panel
           (round 31); a subcontractor's, a supplier's and an "other"'s keep
           the single flat list — a folder structure with one document in each
           is a filing cabinet built for a postcard. */
        // A machine's documents sit on their own tab now (round 34), still
        // drawn as FOLDERS inside the one panel — round 31's rule about the
        // folders is untouched; only the page around them changed.
        (!isAsset || showing('documents')) && (
          <Documents
            kind="resource"
            ownerId={resource.id}
            allowExpiry
            title="Documents"
            folders={isAsset ? ASSET_FOLDERS : undefined}
          />
        )
      )}

      {/* Bookings only, ever. Leave has its own tab (round 36) and belongs
          there: this answers "where is this person going", and an away entry
          is the answer to a different question. */}
      {(isAsset ? showing('schedule') : showing('overview')) && (
      <section className="panel">
        <h2>Coming up</h2>
        {bookings.length === 0 ? (
          <p className="muted">Nothing booked from today onwards.</p>
        ) : (
          <>
            {/* Cards on a phone, table on a desktop — both, because this list
                is being changed and that is the rule (round 34). It had only
                the table, from before it. */}
            <CardList>
              {bookings.map((booking) => (
                <RowCard
                  key={booking.id}
                  to={booking.jobId ? `/app/jobs/${booking.jobId}` : undefined}
                  title={bookingLabel(booking)}
                  badges={
                    booking.inConflict ? (
                      <span className="pill conflict-pill">needs re-planning</span>
                    ) : undefined
                  }
                >
                  <div>{dateRangeLabel(booking.startDate, booking.endDate)}</div>
                  <div>{formatClockRange(booking.startTime, booking.endTime) ?? 'All day'}</div>
                  {booking.note && <div>{booking.note}</div>}
                </RowCard>
              ))}
            </CardList>
            <div className="-mx-6 -mb-6 overflow-x-auto border-t border-gray-200 max-sm:hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    {/* What this entry IS. The table used to open on dates and
                        never say whether they belonged to a job, a machine at
                        the mechanic, or anything else (round 37, item 12). */}
                    <th>What</th>
                    <th>Dates</th>
                    <th>Times</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td className="font-medium">
                        {booking.jobId ? (
                          <Link to={`/app/jobs/${booking.jobId}`} className="table-link">
                            {bookingLabel(booking)}
                          </Link>
                        ) : (
                          bookingLabel(booking)
                        )}
                      </td>
                      <td className="whitespace-nowrap">
                        {dateRangeLabel(booking.startDate, booking.endDate)}
                        {booking.inConflict && (
                          <span className="pill conflict-pill ml-2">needs re-planning</span>
                        )}
                      </td>
                      <td className="table-muted whitespace-nowrap">
                        {formatClockRange(booking.startTime, booking.endTime) ?? 'All day'}
                      </td>
                      <td className="table-muted">{booking.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
      )}
    </>
  );
}

/**
 * Dated unavailability: leave, breakdown, at the mechanic.
 *
 * Adding one on top of existing bookings is allowed on purpose — it saves, and
 * the bookings it lands on are listed straight back so the manager knows what
 * they have just created work for.
 */
function AvailabilityPanel({
  resourceId,
  resourceName,
  entries,
  onChanged,
  onError,
}: {
  resourceId: string;
  resourceName: string;
  entries: Unavailability[];
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const today = todayISO();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [reason, setReason] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // The bookings the leave would land on, from the 409 — non-null while the
  // confirm dialog is open (round 36).
  const [inTheWay, setInTheWay] = useState<BookingInWay[] | null>(null);

  async function add(flags?: { releaseBookings: boolean; allowPastRewrite: boolean }) {
    setBusy(true);
    setFields({});
    try {
      const result = await scheduling.addUnavailability(resourceId, {
        startDate,
        endDate,
        reason,
        ...(flags ?? {}),
      });
      setInTheWay(null);
      setReason('');
      if (result.affectedBookings.length > 0) {
        onChanged(
          `Saved. This lands on ${result.affectedBookings.length} existing booking${
            result.affectedBookings.length === 1 ? '' : 's'
          } (${result.affectedBookings.map(bookingLabel).join(', ')}); they now need re-planning.`
        );
      } else if (flags?.releaseBookings) {
        onChanged(`Saved. ${resourceName} is off the schedule for those days.`);
      } else {
        onChanged('Saved.');
      }
      return true;
    } catch (err) {
      // The warning BEFORE the deed (round 36): nothing was saved, the server
      // answered with the bookings in the way, and the dialog asks what to do
      // about them. Every other error keeps its old path.
      if (err instanceof ApiError && err.code === 'bookings_in_way') {
        setInTheWay((err.details as { bookings: BookingInWay[] }).bookings);
        return false;
      }
      if (err instanceof ApiError) {
        onError(err.message);
        setFields(err.fields);
      } else {
        onError('Could not save that.');
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function remove(entryId: string) {
    try {
      await scheduling.removeUnavailability(resourceId, entryId);
      onChanged('Removed.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not remove that.');
    }
  }

  return (
    <section className="panel">
      <h2>Away</h2>

      {/* The entry form stays at the top and the entries stack below it
          (Nathan, 2026-10-18): adding leave never moves the boxes you type in. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
        noValidate
      >
        <div className="row">
          <Field
            label="From"
            name="unavailFrom"
            type="date"
            value={startDate}
            onChange={(value) => {
              setStartDate(value);
              if (endDate < value) setEndDate(value);
            }}
            error={fields.startDate}
          />
          <Field
            label="To"
            name="unavailTo"
            type="date"
            value={endDate}
            onChange={setEndDate}
            error={fields.endDate}
          />
          <Field
            label="Reason"
            name="reason"
            value={reason}
            onChange={setReason}
            error={fields.reason}
          />
          <button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
      </form>

      {entries.length === 0 ? (
        <p className="muted" style={{ marginTop: 16 }}>
          No leave or downtime recorded.
        </p>
      ) : (
        <div style={{ marginTop: 16 }}>
          {/* Cards on a phone, where the table pushed Remove off the edge;
              the table from `sm` up (the Reps panel's pattern). */}
          <CardList>
            {entries.map((entry) => (
              <RowCard
                key={entry.id}
                title={dateRangeLabel(entry.startDate, entry.endDate)}
                actions={
                  <button
                    type="button"
                    className="row-action-danger"
                    onClick={() => void remove(entry.id)}
                  >
                    Remove
                  </button>
                }
              >
                {entry.reason && <div>{entry.reason}</div>}
              </RowCard>
            ))}
          </CardList>
          <div className="-mx-6 overflow-x-auto border-y border-gray-200 max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dates</th>
                  <th>Reason</th>
                  <th className="w-px" />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="font-medium whitespace-nowrap">
                      {dateRangeLabel(entry.startDate, entry.endDate)}
                    </td>
                    <td className="table-muted">{entry.reason}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="row-action-danger"
                        onClick={() => void remove(entry.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {inTheWay && (
        <LeaveConfirmDialog
          resourceName={resourceName}
          bookings={inTheWay}
          busy={busy}
          onClose={() => setInTheWay(null)}
          onConfirm={(flags) => void add(flags)}
        />
      )}
    </section>
  );
}

/**
 * The leave-over-bookings confirmation (round 36).
 *
 * The server refused the save with the bookings in the way, and this dialog is
 * the ask the old flow never made: the warning used to arrive AFTER the leave
 * was recorded, as "they now need re-planning". Take-off-the-schedule is
 * ticked by default — that is what someone recording leave almost always
 * means — and unticking keeps the old flag-for-replanning behaviour for
 * provisional leave. Days already worked need their own tick (round 16's
 * rule); the Confirm button refuses to send a release the server would refuse
 * anyway, so the rule reads here instead of as a second error.
 */
function LeaveConfirmDialog({
  resourceName,
  bookings,
  busy,
  onClose,
  onConfirm,
}: {
  resourceName: string;
  bookings: BookingInWay[];
  busy: boolean;
  onClose: () => void;
  onConfirm: (flags: { releaseBookings: boolean; allowPastRewrite: boolean }) => void;
}) {
  const [release, setRelease] = useState(true);
  const [pastOk, setPastOk] = useState(false);
  const touchesPast = bookings.some((b) => b.rewritesPast);
  const needsPastTick = release && touchesPast && !pastOk;

  return (
    <Modal
      title={`${resourceName} is booked then`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => onConfirm({ releaseBookings: release, allowPastRewrite: pastOk })}
            disabled={busy || needsPastTick}
          >
            {busy ? 'Saving…' : release ? 'Add leave and update the schedule' : 'Add leave anyway'}
          </button>
        </>
      }
    >
      <div className="legacy-page">
        <p>
          This leave lands on {bookings.length} existing booking
          {bookings.length === 1 ? '' : 's'}:
        </p>
        {/* The BookingModal idiom for a list inside a dialog: bordered,
            divided, capped — same as the cancel-job dialog (round 35). */}
        <ul className="m-0 max-h-56 list-none divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200 p-0">
          {bookings.map((booking) => (
            <li
              key={booking.id}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span className="font-medium text-gray-800">{bookingLabel(booking)}</span>
              <span className="whitespace-nowrap text-gray-600">
                {dateRangeLabel(booking.startDate, booking.endDate)}
              </span>
            </li>
          ))}
        </ul>
        {/* Its own block with real space above — flush against the list it
            reads as part of it (the round-35 lesson). */}
        <label className="checkbox mt-4">
          <input
            type="checkbox"
            checked={release}
            onChange={(e) => setRelease(e.target.checked)}
          />
          Take {resourceName} off the schedule for these days
        </label>
        {release && touchesPast && (
          <label className="checkbox mt-2">
            <input
              type="checkbox"
              checked={pastOk}
              onChange={(e) => setPastOk(e.target.checked)}
            />
            Includes days already worked; change the record too
          </label>
        )}
        <p className="hint">
          {release
            ? 'Booked days outside the leave stay where they are, and the crew is notified.'
            : 'The bookings stay put and are flagged for re-planning instead.'}
        </p>
      </div>
    </Modal>
  );
}
